/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import inputStyles from "../../assets/styles/components/input.module.css";
import styles from "./DocumentSettingsModal.module.css";

import {
  deleteDocumentTypeAssociation,
  getAllProjectDocumentTypes,
  getDocumentTypes,
  registerOrEditDocumentType,
  type EntityTypes,
} from "../../services/documentsManager";
import Button from "../Button/Button";

import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import InputSuspense from "../InputSuspense/InputSuspense";
import ModalGeneric from "../ModalGeneric/ModalGeneric";
import TableContainer from "../TableContainer/TableContainer";
import ModalDelete from "../ModalDelete/ModalDelete";
import { useIsMobile } from "../../utils/useIsMobile";

interface DocumentSettingsModalProps {
  onClose: () => void;
  entidade: EntityTypes;
}

const DocumentSettingsModal = ({
  onClose,
  entidade,
}: DocumentSettingsModalProps) => {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const [deleteTarget, setDeleteTarget] = useState<{ id: string; titulo: string } | null>(null);

  const [newTitle, setNewTitle] = useState("");
  const [isDropDownActive, setIsDropDownActive] = useState(false);

  const entityNames = {
    FINANCPAG: "Contas a Pagar",
    FINANCREC: "Contas a Receber",
    EDUALUNO: "Alunos",
    RHCOLLAB: "Colaboradores",
    FINANCMOV: "Movimentação",
    COMPRAS: "Compras",
    FORNECEDOR: "Fornecedor",
    CUSTOMERS: "Clientes",
  };

  const { data: documentTypes, isLoading } = useQuery({
    queryKey: ["getDocumentTypes", entidade],
    queryFn: () => getDocumentTypes(entidade),
  });

  const { data: allProjectTypes } = useQuery({
    queryKey: ["allProjectDocumentTypes"],
    queryFn: getAllProjectDocumentTypes,
  });

  const suggestionsMemo = useMemo(() => {
    if (!allProjectTypes) return null;

    const existingTitles =
      documentTypes?.map((t: any) => t.titulo.toLowerCase()) || [];
    const filtered = allProjectTypes.filter(
      (t: any) =>
        !existingTitles.includes(t.titulo.toLowerCase()) &&
        t.titulo.toLowerCase().includes(newTitle.toLowerCase()),
    );

    return filtered.map((item: any) => (
      <div
        key={item.uid_tipo_documento}
        className={styles.dropDownItem}
        onClick={() => {
          setNewTitle(item.titulo);
          setIsDropDownActive(false);
        }}
      >
        {item.titulo}
      </div>
    ));
  }, [allProjectTypes, newTitle, documentTypes]);

  useEffect(() => {
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const addMutation = useMutation({
    mutationFn: async (titulo: string) => {
      return await registerOrEditDocumentType({
        titulo,
        grupo: "",
        codigo_entidade: entidade,
      });
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: ["getDocumentTypes", entidade],
      });
      setNewTitle("");
      toast.success(response.message || "Tipo de documento adicionado");
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: { id: string; titulo: string; grupo: string }) => {
      return await registerOrEditDocumentType({
        uid_tipo_documento: data.id,
        titulo: data.titulo,
        grupo: data.grupo,
        codigo_entidade: entidade,
      });
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: ["getDocumentTypes", entidade],
      });
      toast.success(response.message || "Tipo de documento salvo com sucesso");
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const confirmed = window.confirm(
        "Deseja remover este tipo de documento desta lista?",
      );
      if (confirmed) {
        return await deleteDocumentTypeAssociation(id, entidade);
      }
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: ["getDocumentTypes", entidade],
      });
      queryClient.invalidateQueries({ queryKey: ["allProjectDocumentTypes"] });
      toast.success(response.message || "Vínculo removido com sucesso");
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const handleAdd = () => {
    if (!newTitle.trim()) return;
    addMutation.mutate(newTitle);
  };

  return (
    <ModalGeneric
      title={`Configurações: ${entityNames[entidade]}`}
      onClick={onClose}
      modalGenericCustom={styles.width}
    >
      <div className={styles.row}>
        <div className={styles.fieldGroup} style={{ flex: 1 }}>
          <div className={styles.inputWithButton}>
            <InputSuspense
              label=""
              placeholder="Tipo do documento"
              value={newTitle}
              onChange={(e) => {
                setNewTitle(e.target.value);
                setIsDropDownActive(true);
              }}
              isActiveDropDown={isDropDownActive}
              setIsActiveDropDown={setIsDropDownActive}
              option={suggestionsMemo}
              name="new_doc_type"
            />
            <Button
              variant="contain"
              onClick={handleAdd}
              disabled={addMutation.isPending}
            >
              {addMutation.isPending ? "..." : "Adicionar"}
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.containerTable}>
        <TableContainer
          columns={[
            {
              key: "tituloEx",
              titulo: "Título do documento",
              classStyle: styles.columnTitle,
            },
            {
              key: "grupoEx",
              titulo: "Grupo",
              classStyle: styles.columnGroup,
            },
            {
              key: "actionEx",
              titulo: "Ação",
              classStyle: styles.columnAction,
            },
          ]}
          content={
            isLoading
              ? [
                  {
                    values: {
                      tituloEx: "Carregando tipos...",
                      grupoEx: "",
                    },
                    rowClass: styles.columnEmpty,
                    buttons: [],
                  },
                ]
              : (documentTypes?.map((type: any) => ({
                  values: {
                    tituloEx: (
                      <input
                        className={`${inputStyles.input} ${styles.tableInputOverride}`}
                        defaultValue={type.titulo}
                        onBlur={(e) => {
                          if (
                            e.target.value !== type.titulo &&
                            e.target.value.trim() !== ""
                          ) {
                            updateMutation.mutate({
                              id: type.uid_tipo_documento,
                              titulo: e.target.value,
                              grupo: type.grupo,
                            });
                          }
                        }}
                      />
                    ),
                    grupoEx: (
                      <input
                        className={`${inputStyles.input} ${styles.tableInputOverride}`}
                        defaultValue={type.grupo}
                        onBlur={(e) => {
                          if (
                            e.target.value !== type.grupo &&
                            e.target.value.trim() !== ""
                          ) {
                            updateMutation.mutate({
                              id: type.uid_tipo_documento,
                              titulo: type.titulo,
                              grupo: e.target.value,
                            });
                          }
                        }}
                      />
                    ),
                  },
                  buttons: [
                    {
                      variant: "contain",
                      typeStyle: ["small", "cancel"],
                      title: "Excluir tipo de documento",
                      // AQUI: Apenas seta o estado para abrir o modal
                      onClick: () => setDeleteTarget({ id: type.uid_tipo_documento, titulo: type.titulo }),
                      disabled: deleteMutation.isPending,
                      icon: <DeleteIcon />,
                    },
                  ],
                })) ?? [])
          }
        />
      </div>
      
      {/* MODAL DE DELEÇÃO */}
      <ModalDelete
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        isDeleting={deleteMutation.isPending}
        entity="tipo de documento"
        title={deleteTarget?.titulo || ""}
        isMobile={isMobile}
      />
    </ModalGeneric>
  );
};

export default DocumentSettingsModal;
